import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Hash, Loader, MessagesSquare, Plus } from 'lucide-react';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import {
  DiscordTargets,
  ModpackConfigEnv,
  ProvisionResult,
} from '../../types/modpackConfig';
import { SectionHeader } from '../ui/SectionHeader';
import { cn } from '../../utils/cn';

interface ProvisionDiscordPanelProps {
  address: string;
  env: ModpackConfigEnv;
  /** Rendered inside a card already, so the panel does not add another. */
  bare?: boolean;
  onProvisioned?: (result: ProvisionResult) => void;
}

type Mode = 'attach' | 'create';

/**
 * Giving a pack its Discord forum.
 *
 * Mirrors the two paths /addmodpack's wizard offers — attach a forum that
 * already exists, or create one in a configured chat category — because that
 * choice is the wizard's, not an invention here.
 *
 * Deliberately separate from creating the config row. Provisioning talks to
 * Discord and can fail on its own, and a pack whose row exists but whose forum
 * does not is recoverable; one where the failure lost both is not. So this is
 * reachable from the pack's own page as well as from the launch flow.
 *
 * The role is not created here: Mr. Roboto's hourly pass owns pack roles and
 * creates any that are missing.
 */
export const ProvisionDiscordPanel = ({
  address, env, bare, onProvisioned,
}: ProvisionDiscordPanelProps) => {
  const [targets, setTargets] = useState<DiscordTargets | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('attach');
  const [choice, setChoice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ProvisionResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    modpackConfigsApiService.getDiscordTargets()
      .then((data) => {
        if (cancelled) return;
        setTargets(data);
        // Start on whichever path the guild can actually offer.
        if (data.forums.length === 0 && data.categories.length > 0) setMode('create');
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load Discord channels');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setChoice(''); }, [mode]);

  const handleProvision = async () => {
    if (!choice) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await modpackConfigsApiService.provision(
        address,
        mode === 'attach' ? { channel_id: choice } : { category_id: choice },
        env,
      );
      setDone(result);
      onProvisioned?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to provision');
    } finally {
      setSubmitting(false);
    }
  };

  const options = mode === 'attach' ? (targets?.forums ?? []) : (targets?.categories ?? []);

  const body = (
    <>
      <SectionHeader
        title="Discord forum"
        icon={MessagesSquare}
        description="Attach the forum this pack's chat lives in, or create one. The pack's role is created separately by Mr. Roboto's hourly pass."
      />

      {loading ? (
        <p className="text-xs text-glass-400">Loading channels…</p>
      ) : loadError ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      ) : done ? (
        <div className="alert alert-success" role="status">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>
            {done.channel_created ? 'Forum created' : 'Forum attached'}
            {done.channel_id && <> — <span className="font-mono">{done.channel_id}</span></>}.
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="segmented max-w-full">
            <button
              type="button"
              className="segmented-item"
              data-active={mode === 'attach'}
              onClick={() => setMode('attach')}
              disabled={(targets?.forums.length ?? 0) === 0}
            >
              <Hash className="w-4 h-4" />
              Use existing
            </button>
            <button
              type="button"
              className="segmented-item"
              data-active={mode === 'create'}
              onClick={() => setMode('create')}
              disabled={(targets?.categories.length ?? 0) === 0}
            >
              <Plus className="w-4 h-4" />
              Create new
            </button>
          </div>

          {options.length === 0 ? (
            <p className="text-xs text-glass-400">
              {mode === 'attach'
                ? 'No forum channels exist in the guild.'
                : 'No chat categories are configured, so a forum cannot be created. Set CHAT_CATEGORY_IDS in the bot config.'}
            </p>
          ) : (
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              className="glass-select w-full"
              aria-label={mode === 'attach' ? 'Forum to attach' : 'Category to create the forum in'}
            >
              <option value="">
                {mode === 'attach' ? 'Pick a forum…' : 'Pick a category…'}
              </option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}

          <button
            type="button"
            onClick={handleProvision}
            disabled={submitting || !choice}
            className={cn('btn-primary', bare && 'w-full sm:w-auto')}
          >
            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <MessagesSquare className="w-4 h-4" />}
            {mode === 'attach' ? 'Attach forum' : 'Create forum'}
          </button>
        </div>
      )}
    </>
  );

  return bare ? <div className="space-y-3">{body}</div> : <div className="subcard p-4">{body}</div>;
};
